import {useMemo} from 'react';
import {useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import Select from 'react-select';
import {NotificationManager} from 'react-notifications';
import {newGraySelectStyles} from '../../../common/shared/select.styled';
import CacheData from '../../../common/utils/cacheData';
import Button from '../../../common/Button';
import Datepicker from '../../../common/DatepickerPortal';
import ServerData from '../../../common/serverData';
import {CurrencyValue} from '../../../common/currencyValue';
import {constants} from '../../../common/constants/constants';
import {hideLoader, showLoader} from '../../../store/common/slice';
import arrowInCircle from '../../../images/newDesign/arrow_in_white_circle.svg';
import PaymentFlowContainer from '../PaymentFlowContainer';
import {StyledCheckoutPage} from './CheckoutPage.styled';
import { MonthlyCheckoutItem } from './MonthlyCheckoutItem';
import { SpecialCheckoutItem } from './SpecialCheckoutItem';
import {setChargeDate, setChargeDay, setPaymentMethod, setPaymentMethodId, setPaymentPlan} from "../../../store/tenant/slice";
import {DebtCheckoutItem} from "./DebtCheckoutItem";
import {GAEvent} from "../../../common/google_analytics/google_analytics";
import { CommissionText } from '../../../common/order_status/upComingMonths';


const fixedDaysOptions = Array.from({ length: 28 }, (v, k) => ({ value: k+1, label: k+1 }));

export function CheckoutPage({ match }) {

    const { buildingID, apartmentNumber } = match.params;

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();

    const ltr = useSelector((state) => state.common.ltrEnabled);

    const bitPaymentsDisabled = useSelector(state => state.tenant.buildingBitPaymentsDisabled ?? false);
    const applePayPaymentsDisabled = !useSelector(state => state.tenant.buildingMobilePaymentsDisabled ?? false);
    const bankChargeDisabled = useSelector(state => state.tenant.buildingBankChargeDisabled ?? false);

    const paymentOptions = [
        { value: 'creditCards', label: t('buildings.creditCards'), id: 1 },

    ];

    !bitPaymentsDisabled && paymentOptions.push({ value: constants.APIContract.paymentMethodsKeys.bit, label: t('payments.bit'), id: constants.APIContract.paymentMethods.bit });
    !applePayPaymentsDisabled && paymentOptions.push({ value: constants.APIContract.paymentMethodsKeys.applePay, label: t('payments.applePay'), id: constants.APIContract.paymentMethods.applePay });
    !bankChargeDisabled && paymentOptions.push({ value: constants.APIContract.paymentMethodsKeys.bankCharge, label: t('buildings.bankCharge'), id: 6 },)

    const currency = useSelector(state => state.tenant.buildingCurrency);
    const tenantCommission = useSelector(state => state.tenant.tenantCommission);
    const debtSelectedPayments = useSelector(state => state.tenant.debtSelectedPayments);
    const onGoingSelectedPayments = useSelector(state => state.tenant.onGoingSelectedPayments);
    const oneTimeSelectedPayments = useSelector(state => state.tenant.oneTimeSelectedPayments);
    const paymentMethod = useSelector(state => state.tenant.paymentMethod);
    const paymentMethodId = useSelector(state => state.tenant.paymentMethodId);
    const createPaymentPlanPayload = useSelector(state => state.tenant.createPaymentPlanPayload);
    const firstPayments = useSelector(state => state.tenant.firstPayment);
    const futurePaymentsDisabledByPaymentType = useSelector(state => state.tenant.futurePaymentsDisabledByPaymentType);
    const currentBuildingData = useSelector(state => state.buildings.currentBuildingData);

    const installments = useSelector(state => state.tenant.installments);
    const installmentsDisabled = useSelector(state => {
        const { installmentParams: { onGoing, debts, oneTime } } = state.tenant;
        return onGoing?.disabled || debts?.disabled || Object.values(oneTime).some(p => p.disabled);
    });

    const firstPaymentTotal = useMemo(
        () => (firstPayments.debts || 0) + (firstPayments.onGoing || 0) + Object.values(firstPayments.oneTime).reduce((acc, v) => acc + v, 0),
        [firstPayments]
    );

    const chargeDay = useSelector(state => state.tenant.chargeDay);
    const chargeDate = useSelector(state => new Date(state.tenant.chargeDate));
    const chargeDateMin = useSelector(state => new Date(state.tenant.chargeDateMin));
    const chargeDateMax = useSelector(state => new Date(state.tenant.chargeDateMax));

    const areNumsOfInstallmentsEqual1 = useMemo(
        () => {
            if (installments.debts > 1) return false;
            if (installments.onGoing > 1) return false;
            for (const oneTimeUid in installments.oneTime) {
                if (installments.oneTime[oneTimeUid] > 1) return false;
            }
            return true;
        },
        [installments],
    )

    const paymentMethodOption = useMemo(() => paymentOptions.find(o => o.value === paymentMethod), [paymentMethod]);
    const fixedDaysOption = useMemo(() => fixedDaysOptions.find(o => o.value === chargeDay), [chargeDay])

    const totalCommission = getExtraCommission(paymentMethod) + tenantCommission;
    const isContinueDisabled = useMemo(() => checkToDisableContinueButton(paymentMethod, firstPaymentTotal, totalCommission), [paymentMethod, totalCommission, firstPaymentTotal])

    function handlePaymentMethodChange(e) {
        dispatch(setPaymentMethod(e.value));
        dispatch(setPaymentMethodId(e.id));
    }

    function handleChargeDateChange(date) {
        dispatch(setChargeDate(date.toJSON()));
    }

    function handleChargeDayChange(item) {
        dispatch(setChargeDay(item.value));
    }

    async function handleContinueClick() {
        const isBitPayment = paymentMethod === constants.APIContract.paymentMethodsKeys.bit;
        if (paymentMethod === constants.APIContract.paymentMethodsKeys.bankCharge) {
            history.push({pathname: `/${constants.pages.bankPage}/${buildingID}`})
        } else if (paymentMethod === 'creditCards' || isBitPayment) {
            GAEvent(constants.GoogleAnalytics.Events.payment, 'clickedPayOnCheckoutPage', 1, 'clickedPayOnCheckoutPage');
            const client = new ServerData();
            const payload = {...createPaymentPlanPayload};
            payload.isBitPayment = isBitPayment;
            try {
                dispatch(showLoader());
                const paymentPlan = await client.tenants.payments.createPaymentPlan(buildingID, apartmentNumber, payload);
                const cartID = paymentPlan?.cartID;
                if (!cartID) {
                    // TODO: use error code and translate it after?
                    throw new Error(t('payments.payment_plan_was_not_created'));
                }
                if (isBitPayment) {
                    dispatch(setPaymentPlan(paymentPlan));
                    history.push(`/${constants.pages.bitPage}/${buildingID}/${cartID}`);
                } else {
                    if (paymentPlan.currency === 'MXN') {
                        // NOTE: paymentDetails page is old one component it do not use redux store
                        CacheData.cache('createCartResponse', paymentPlan, {cartID: paymentPlan.cartID});
                        history.push(`/${constants.pages.paymentDetailsRapydUrl}/${paymentPlan.cartID}`);
                    } else {
                        dispatch(setChargeDate(chargeDate));
                        dispatch(setPaymentPlan(paymentPlan));
                        history.push(`/${constants.pages.paymentDetailsUrl}/${buildingID}/${paymentPlan.cartID}`);
                    }
                }
            } catch (e) {
                // TODO: use translated message
                NotificationManager.error(e.message, '', 5000);
            } finally {
                dispatch(hideLoader());
            }
        }
    }

    function checkToDisableContinueButton(paymentMethod, amount, commissionInPercent) {
        const amountInIls = amount / 100;
        const commission = (amountInIls * commissionInPercent) / 100;
        const totalAmountInIls = amountInIls + commission;
        return paymentMethod === constants.APIContract.paymentMethodsKeys.bit &&
            (totalAmountInIls > constants.bitRange.max || totalAmountInIls < constants.bitRange.min);
    }

    function getExtraCommission(paymentMethod) {
        let totalCommission = 0;
        if (paymentMethod === constants.APIContract.paymentMethodsKeys.bit){
            totalCommission += constants.commissions.BIT_COMMISSION;
        }
        return totalCommission;
    }

    return (
        <PaymentFlowContainer title={t('payments.payment_for')} subheader={t('payments.checkout_info')}>


            <StyledCheckoutPage ltr={ltr}>
                <div className='payment-page'>{t('payments.payment_page')}</div>
                
                <div className='credit-card-and-actions-block'>
                    {debtSelectedPayments?.length > 0 && <DebtCheckoutItem payments={debtSelectedPayments} />}
                    {onGoingSelectedPayments?.length > 0 && <MonthlyCheckoutItem payments={onGoingSelectedPayments} />}
                    {oneTimeSelectedPayments?.length > 0 && <SpecialCheckoutItem payments={oneTimeSelectedPayments} />}
                </div>

                <div className='payment-info'>

                    {!futurePaymentsDisabledByPaymentType &&  <div className='dropdown-wrapper'>
                        <label className='checkout-label' htmlFor="apartmentNumber">{t('payments.first_date')}</label>
                        <Datepicker
                            date={chargeDate}
                            minDate={chargeDateMin}
                            maxDate={chargeDateMax}
                            placeholder='dd/mm/yyyy'
                            onChange={handleChargeDateChange}
                            ltr={ltr}
                            name='date'
                            withPortal={false}
                            showArrow={false}
                            className='payment-flow'
                        />
                    </div>}

                    {!areNumsOfInstallmentsEqual1 && <div className='checkout-label dropdown-wrapper'>
                        <div>{t('payments.fixed_day')}</div>
                        <Select
                            styles={newGraySelectStyles}
                            classNamePrefix='dropdown'
                            options={fixedDaysOptions}
                            value={fixedDaysOption}
                            onChange={handleChargeDayChange}
                            isSearchable={false}
                        />
                    </div>}

                    <div className='checkout-label payment-type'>{t('expenses.payment_type')}</div>
                    {installmentsDisabled && (<div className='disable-installment-note'>{t('payments.installmentDisabled')}</div>)}
                    <div>
                        <Select 
                            styles={newGraySelectStyles}
                            classNamePrefix='dropdown'
                            options={paymentOptions}
                            value={paymentMethodOption}
                            onChange={handlePaymentMethodChange}
                            isSearchable={false}
                        />
                    </div>
                </div>

                <div className={'total-first-payment-wrapper'}>
                    <div className='total-first-payment'>
                        <span>{t('payments.first_payment')}:</span>
                        <CurrencyValue currency={currency}>{firstPaymentTotal}</CurrencyValue>
                    </div>
                    {isContinueDisabled && <div className={'out-of-range'}>{t('expenses.set_expenses.amount_out_of_range', {min: constants.bitRange.min, max: constants.bitRange.max })}</div>}
                </div>

                <div className='payment-note'>
                    <CommissionText 
                        firstPayment={{total: firstPaymentTotal, currency}}
                        tenantCommission={totalCommission}
                        paymentMethodId={paymentMethodId}
                    />
                    {paymentMethodId === 6 && (
                        <div className='bank-comission-note'>{t('expenses.bank_comission')}</div>
                    )}
                </div>

                <div className='btn-wrapper'>
                    <Button onClick={handleContinueClick} disabled={isContinueDisabled}>
                        {t('expenses.set_expenses.continue')} <img className='arrow' src={arrowInCircle} />
                    </Button>
                </div>
            </StyledCheckoutPage>

        </PaymentFlowContainer>
    )
}
